// Centralized read of Vite's import.meta.env so component code never
// touches it directly. One change point if env-var names shift.
//
// VITE_PRIVY_APP_ID         — required in production. Empty string in
//                              dev means "no real Privy session"; the
//                              UI still renders, but login is a no-op.
// VITE_API_BASE_URL         — empty → same-origin (Vite proxy or
//                              same Cloud Run Service). Otherwise a
//                              fully-qualified projector URL.
// VITE_DEV_AUTH_BYPASS      — frontend mirror of the projector's
//                              DEV_AUTH_BYPASS. When 'true', the API
//                              client skips token injection so the
//                              projector's synthetic identity is used.

import type { ChainEnv } from '@rmint/ui';

type Env = {
  privyAppId: string;
  apiBaseUrl: string;
  devAuthBypass: boolean;
  // Phase 7 §7.4 — on-chain reads.
  chainEnv: ChainEnv;
  rpcUrl: string;
  // Phase 7 Slice F — Stripe publishable key for browser-side stripe.js
  // initialization. Test-mode key in dev/staging; production cutover
  // is Phase 8/9. Missing key → widget surfaces error at click time
  // (lib/env.ts is read at module load, but the widget only fails when
  // the user actually clicks the verify CTA).
  stripePublishableKey: string;
};

function pickChainEnv(raw: string | undefined): ChainEnv {
  if (raw === 'local' || raw === 'base-sepolia' || raw === 'base') return raw;
  // Default to Base Sepolia for dev — public RPC, free, no key needed.
  return 'base-sepolia';
}

function defaultRpcFor(chainEnv: ChainEnv): string {
  switch (chainEnv) {
    case 'local':
      return 'http://127.0.0.1:8545';
    case 'base':
      return 'https://mainnet.base.org';
    case 'base-sepolia':
      return 'https://sepolia.base.org';
  }
}

const chainEnv = pickChainEnv(import.meta.env.VITE_CHAIN_ENV);

export const env: Env = {
  privyAppId: import.meta.env.VITE_PRIVY_APP_ID ?? '',
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL ?? '',
  devAuthBypass: import.meta.env.VITE_DEV_AUTH_BYPASS === 'true',
  chainEnv,
  rpcUrl: import.meta.env.VITE_RPC_URL ?? defaultRpcFor(chainEnv),
  stripePublishableKey: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? '',
};
